import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Map, ClipboardCheck, BarChart2, BookOpen, Settings, ChevronRight } from 'lucide-react';
import { reachConfigAPI, ReachEngagementConfig } from '../../services/reachService';
import { REACHStageBadge } from '../../components/reach/common/REACHStageBadge';

const REACH_STAGES = ['R', 'E', 'A', 'C', 'H'] as const;
const STAGE_NAMES: Record<string, string> = {
  R: 'Recognise / Rally',
  E: 'Engage / Enable',
  A: 'Acquire / Architect',
  C: 'Capability / Cultivate',
  H: 'Habits / Hardwire',
};

const TOOLS = [
  { label: 'Stakeholder Map', icon: Map, path: 'stakeholder-map', description: 'Drag-drop influence × readiness map with REACH zone toolbox.' },
  { label: 'Assessments', icon: ClipboardCheck, path: 'assessments', description: 'Role-specific REACH readiness assessments — champion, executive, user, practice leader.' },
  { label: 'Results Dashboard', icon: BarChart2, path: 'results', description: 'Heat map, individual vs organisational gap analysis, respondent table.' },
];

const REACHProjectHome: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [config, setConfig] = useState<ReachEngagementConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reachConfigAPI.get(projectId!)
      .then(r => setConfig(r.data.config))
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <BookOpen size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Change Management</h1>
              <p className="text-slate-500 text-sm font-medium">
                {loading ? '…' : config ? `${config.programmeName} · ${config.customerOrg}` : 'Configure your engagement to get started'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/projects/${projectId}/reach/config`)}
            className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            <Settings size={16} /> Configure
          </button>
        </header>

        {/* Config incomplete banner */}
        {!loading && !config && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-4">
            <Settings size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Engagement not yet configured</p>
              <p className="text-sm text-amber-700 mt-0.5">Complete the engagement configuration before running assessments or mapping stakeholders.</p>
              <button
                onClick={() => navigate(`/projects/${projectId}/reach/config`)}
                className="mt-3 text-sm font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900"
              >
                Go to configuration →
              </button>
            </div>
          </div>
        )}

        {/* REACH stage overview */}
        {config && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
            <h2 className="text-sm font-bold text-navy mb-4">REACH Framework</h2>
            <div className="grid grid-cols-5 gap-3">
              {REACH_STAGES.map(stage => (
                <div key={stage} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <REACHStageBadge stage={stage} showLabel={false} size="md" />
                  <p className="text-xs font-semibold text-navy text-center">{STAGE_NAMES[stage]}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tools grid */}
        <div className="grid grid-cols-3 gap-4">
          {TOOLS.map(tool => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.path}
                onClick={() => navigate(`/projects/${projectId}/reach/${tool.path}`)}
                disabled={!config}
                className="flex flex-col items-start gap-3 p-5 bg-white border border-[#e2e8f0] rounded-2xl text-left hover:shadow-md hover:border-primary/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-navy">{tool.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{tool.description}</p>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-primary transition-colors self-end" />
              </button>
            );
          })}
        </div>

        {/* Engagement details */}
        {config && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0]">
              <h2 className="text-sm font-bold text-navy">Engagement Details</h2>
            </div>
            <div className="p-5 grid grid-cols-3 gap-4">
              {[
                { label: 'Programme', value: config.programmeName },
                { label: 'Customer', value: config.customerOrg },
                { label: 'Duration', value: `${config.engagementDurationWeeks} weeks` },
                { label: 'Start Date', value: config.startDate },
                { label: 'Sponsor', value: config.sponsorName },
                { label: 'Comms Owner', value: config.commsOwner },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-[11px] font-bold text-primary tracking-widest uppercase">{label}</p>
                  <p className="text-sm font-medium text-navy mt-0.5">{value || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default REACHProjectHome;
